import { memo, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '@shadcn/ui/components/ui/button';
import { Input } from '@shadcn/ui/components/ui/input';
import { cn } from '@shadcn/ui/lib/utils';
import { Star, Search, LineChart, SmilePlus, UserSearch, MessageCirclePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { WeightKeys } from '@andsend/intelligence-shared';

interface SimpleMessageFiltersProps {
    activeFilter: WeightKeys | 'urgent';
    onFilterChange: (filter: WeightKeys | 'urgent') => void;
    onCustomFilterSubmit?: (filter: string) => Promise<void> | void;
    hasInvestorConversations?: boolean;
    className?: string;
}

const FILTER_OPTIONS: {
    key: WeightKeys | 'urgent';
    label: string;
    icon: React.ElementType;
}[] = [
    {
        key: 'urgent',
        label: "Today's Actions",
        icon: Star,
    },
    {
        key: 'customerSuccess',
        label: 'Customer Success',
        icon: SmilePlus,
    },
    {
        key: 'fundraising',
        label: 'Fundraising',
        icon: LineChart,
    },
    {
        key: 'networking',
        label: 'Networking',
        icon: MessageCirclePlus,
    },
    {
        key: 'recruiting',
        label: 'Recruitment',
        icon: UserSearch,
    },
    {
        key: 'sales',
        label: 'Sales',
        icon: Star,
    },
];

export const SimpleMessageFilters = memo(
    ({
        activeFilter,
        onFilterChange,
        onCustomFilterSubmit,
        hasInvestorConversations = false,
        className,
    }: SimpleMessageFiltersProps) => {
        const [customFilter, setCustomFilter] = useState<string>('');
        const [isAiFilterExpanded, setIsAiFilterExpanded] = useState(false);
        const inputRef = useRef<HTMLInputElement>(null);

        // Filter out investors option if there are no investor conversations
        const availableFilterOptions = useMemo(() => {
            return FILTER_OPTIONS;
        }, [hasInvestorConversations]);

        const handleFilterClick = useCallback(
            (filter: WeightKeys | 'urgent') => {
                onFilterChange(filter);
                setIsAiFilterExpanded(false);
            },
            [onFilterChange]
        );

        const handleCustomFilterSubmit = async (e?: React.FormEvent) => {
            if (e) {
                e.preventDefault();
            }

            if (customFilter.trim()) {
                if (onCustomFilterSubmit) {
                    await onCustomFilterSubmit(customFilter);
                }
                onFilterChange('fundraising');
                setIsAiFilterExpanded(false);
            }
        };

        const toggleAiFilter = () => {
            setIsAiFilterExpanded(!isAiFilterExpanded);
            if (!isAiFilterExpanded && inputRef.current) {
                // Focus the input after a short delay to ensure it's visible
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 300);
            }
        };

        // Close AI filter when clicking outside
        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (isAiFilterExpanded && inputRef.current && !inputRef.current.contains(event.target as Node)) {
                    setIsAiFilterExpanded(false);
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [isAiFilterExpanded]);

        return (
            <div className={cn('relative flex items-center gap-2 w-full', className)}>
                <AnimatePresence mode="wait">
                    {!isAiFilterExpanded ? (
                        <motion.div
                            key="standard-filters"
                            initial={{ opacity: 1 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex items-center gap-2 w-full"
                        >
                            <div className="flex items-center gap-2">
                                {availableFilterOptions.map(({ key, label, icon: Icon }) => (
                                    <Button
                                        key={key}
                                        variant={activeFilter === key ? 'default' : 'ghost'}
                                        size="sm"
                                        onClick={() => handleFilterClick(key)}
                                        className={cn(
                                            'h-8 px-3 text-sm font-medium rounded-full transition-all duration-200',
                                            'flex items-center gap-2',
                                            activeFilter === key
                                                ? 'bg-background-brand-primary text-white hover:bg-background-brand-primary/90'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                                        )}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {label}
                                    </Button>
                                ))}
                            </div>

                            {/* <div className="ml-auto">
                                <div
                                    onClick={toggleAiFilter}
                                    className={cn(
                                        'rounded-full transition-all border flex items-center gap-2 cursor-pointer h-8 px-3 py-1.5',
                                        'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
                                        activeFilter === 'custom'
                                            ? 'bg-purple-600 text-white border-purple-600'
                                            : ''
                                    )}
                                >
                                    <Sparkles className="h-4 w-4 shrink-0" />
                                    <span className="text-sm font-medium whitespace-nowrap">AI Filter</span>
                                </div>
                            </div> */}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="ai-filter"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="w-full"
                        >
                            <form onSubmit={handleCustomFilterSubmit} className="w-full">
                                <div
                                    onClick={toggleAiFilter}
                                    className="rounded-full bg-purple-100 text-purple-800 border border-purple-200 hover:bg-purple-200 w-full py-1 px-3 flex items-center gap-2 cursor-pointer h-8"
                                >
                                    <Search className="h-4 w-4 shrink-0" />
                                    <Input
                                        ref={inputRef}
                                        value={customFilter}
                                        onChange={(e) => setCustomFilter(e.target.value)}
                                        placeholder="e.g., Messages about funding or partnerships"
                                        className="border-none bg-transparent p-0 h-6 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm placeholder:text-purple-600/70 flex-1 min-w-0"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    {customFilter && (
                                        <Button
                                            type="submit"
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 px-2 ml-auto text-purple-800 hover:text-purple-900 hover:bg-purple-200 shrink-0"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCustomFilterSubmit();
                                            }}
                                        >
                                            Search
                                        </Button>
                                    )}
                                </div>
                            </form>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }
);

SimpleMessageFilters.displayName = 'SimpleMessageFilters';
